import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ExchangeHeroGraphic } from "@/components/ExchangeHeroGraphic";

// Shared landing for the Surety exchange pages (Construction Bond / Commercial
// Bonds). Same shape as the API Exchange — a light hero with a crimson eyebrow,
// an optional primary CTA, and a badged product grid. Content is passed in so
// the two exchange tabs stay DRY. Presentational: the Bond Exchange is Signal's
// own surety MGA program (lives in PMS, not built yet), so product tiles are
// inert and a page with no `cta` shows an inert "Coming soon" button.
//
// BRK-S174 added two things and kept it presentational. `secondaryCta` is an
// action whose FEATURE does not exist yet — rendered, plainly disabled, with a
// one-line note and NO date, because a placeholder promising a date is a
// commitment nobody made (the account profile's parked-tab rule). And
// `children` is a slot for whatever a particular exchange puts between the hero
// and the product grid; the Construction page puts the brokerage's own request
// list there.

export interface BondProduct {
  name: string;
  blurb: string;
  status: "active" | "coming_soon";
}

interface BondExchangeLandingProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Primary CTA. Omit for a page whose flow isn't wired yet (renders inert). */
  cta?: { label: string; to: string };
  /**
   * An action a broker will have and does not yet — rendered beside the primary
   * CTA, visibly disabled, with `note` under it. It is SHOWN rather than
   * hidden for the reason the parked account tabs are: hiding it makes the page
   * look finished and then grows a control a broker has to rediscover. The note
   * says what it is and never when.
   */
  secondaryCta?: { label: string; note: string };
  sectionTitle: string;
  products: BondProduct[];
  /** Rendered between the hero and the product grid — see the header. */
  children?: ReactNode;
}

// Small crimson four-chevron mark used to badge the section header.
function SignalTick() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="-32 -32 64 64"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-brk-accent"
    >
      <g
        stroke="currentColor"
        strokeWidth="9"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16.8 3 L26.8 -26.8 L-3 -17" />
        <path d="M-16.8 3 L-26.8 -26.8 L3 -17" />
        <path d="M16.8 -3 L26.8 26.8 L-3 17" />
        <path d="M-16.8 -3 L-26.8 26.8 L3 17" />
      </g>
    </svg>
  );
}

function BondCard({ product }: { product: BondProduct }) {
  const active = product.status === "active";
  return (
    <li className="group flex flex-col gap-6 rounded-2xl border border-brk-border bg-brk-panel p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <span
          aria-hidden="true"
          className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brk-accent/10 text-brk-accent"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 5h13a1 1 0 0 1 1 1v13a1 1 0 0 1-1.5.87L14 18l-2.5 1.9L9 18l-2.5 1.9L4 18V5Zm4 4h8M8 12h6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {active ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            <span aria-hidden="true">●</span> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brk-bg px-2.5 py-0.5 text-xs font-medium text-brk-dim">
            <span aria-hidden="true">●</span> Coming soon
          </span>
        )}
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-brk-text">{product.name}</span>
          <span className="text-sm text-brk-dim">{product.blurb}</span>
        </div>
        <span
          aria-hidden="true"
          className="text-brk-dim transition-colors group-hover:text-brk-accent"
        >
          →
        </span>
      </div>
    </li>
  );
}

export function BondExchangeLanding({
  eyebrow,
  title,
  subtitle,
  cta,
  secondaryCta,
  sectionTitle,
  products,
  children,
}: BondExchangeLandingProps) {
  return (
    <div className="min-h-full bg-brk-bg px-4 py-8 text-brk-text sm:px-8 sm:py-10">
      <div className="flex flex-col gap-10">
        {/* Hero — mirrors the API Exchange landing. */}
        <section className="relative">
          <ExchangeHeroGraphic className="pointer-events-none absolute -top-2 right-0 hidden w-[30rem] max-w-[46%] lg:block" />
          <div className="relative z-10 flex max-w-2xl flex-col gap-4">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brk-accent">
              {eyebrow}
            </p>
            <h1 className="text-3xl font-bold tracking-tight text-brk-text sm:text-5xl">
              {title}
            </h1>
            <p className="max-w-prose text-brk-dim">{subtitle}</p>
            {/* Mobile first: the actions stack full-width at 375 and sit side
                by side from `sm:` up. */}
            <div className="flex flex-col items-stretch gap-3 pt-1 sm:flex-row sm:items-start">
              {cta ? (
                <Link
                  to={cta.to}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brk-accent px-6 py-3 font-medium text-white transition-colors hover:bg-brk-accent-strong"
                >
                  <span aria-hidden="true" className="text-lg leading-none">
                    +
                  </span>
                  {cta.label}
                </Link>
              ) : (
                // No flow wired for this exchange yet — inert placeholder.
                <button
                  type="button"
                  aria-disabled="true"
                  title="Coming soon"
                  className="inline-flex cursor-default items-center justify-center gap-2 rounded-lg bg-brk-accent/60 px-6 py-3 font-medium text-white"
                >
                  <span aria-hidden="true" className="text-lg leading-none">
                    +
                  </span>
                  Start submission
                  <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium">
                    Coming soon
                  </span>
                </button>
              )}
              {secondaryCta && (
                <div className="flex flex-col gap-1">
                  {/* REALLY disabled, not merely styled: `disabled` keeps it out
                      of the tab order and out of a screen reader's list of
                      things to press, which `aria-disabled` alone would not. */}
                  <button
                    type="button"
                    disabled
                    className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-brk-border px-6 py-3 font-medium text-brk-dim"
                  >
                    {secondaryCta.label}
                  </button>
                  <p className="text-xs text-brk-dim">{secondaryCta.note}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Whatever this exchange puts between the hero and the catalogue. The
            Construction page puts the brokerage's own request list here, ABOVE
            the product tiles on purpose: a broker returning to their front door
            wants to know where their requests stand, and the tiles are what they
            read the first time. */}
        {children}

        <hr className="border-brk-border" />

        <section className="flex flex-col gap-4">
          <h2 className="flex items-center gap-2.5 text-lg font-semibold text-brk-text">
            <SignalTick />
            {sectionTitle}
          </h2>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <BondCard key={p.name} product={p} />
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
